import { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { getFilters } from '../../../../../services/savedFiltersService';
import { userAppContext } from '../../../../../Context/Context';
import { UserContext } from '../../../../../types';

interface SavedScore {
  id?: string;
  key?: string;
  name: string;
  sourceWeights: Array<{ name: string; score: number }>;
  savedAt: string;
  searchContext?: {
    view: string;
    tab: string;
  };
}

interface SavedScoresDropdownProps {
  tab: string;
  onApplyScore: (score: SavedScore) => void;
  onDeleteScore: (id: string) => void;
  onConfigureNew: () => void;
  onClearWeights: () => void;
  hasActiveWeights: boolean;
  refreshTrigger?: number;
}

export default function SavedScoresDropdown({
  tab,
  onApplyScore,
  onConfigureNew,
  onClearWeights,
  hasActiveWeights,
  refreshTrigger = 0,
}: SavedScoresDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [savedScores, setSavedScores] = useState<SavedScore[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, user } = userAppContext() as unknown as UserContext;
  const isTrial = Array.isArray(user?.['cognito:groups']) && user['cognito:groups'].includes('ROLE_TRIAL');

  useEffect(() => {
    loadSavedScores();
  }, [tab, refreshTrigger]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const loadSavedScores = async () => {
    const scores: SavedScore[] = [];

    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('savedScore_')) {
        try {
          const score = JSON.parse(localStorage.getItem(key) || '');
          scores.push({
            key,
            ...score,
          });
        } catch (error) {
          console.error('Error parsing saved score:', error);
        }
      }
    });

    if (isAuthenticated) {
      try {
        const response = await getFilters();
        if (response.filters && Array.isArray(response.filters)) {
          const scoreFilters = response.filters
            .filter(f => f.type === 'score')
            .map(f => ({
              id: f.id,
              name: f.name,
              sourceWeights: f.sourceWeights,
              savedAt: f.createdAt || f.savedAt,
              searchContext: f.searchContext,
            }));
          scores.push(...scoreFilters);
        }
      } catch (error) {
        console.error('Error loading saved scores from DynamoDB:', error);
      }
    }

    const filteredScores = scores.filter(score => {
      if (!score.searchContext) return true;
      return score.searchContext.tab === tab;
    });

    filteredScores.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());

    setSavedScores(filteredScores);
  };

  return (
    <div ref={dropdownRef} className="relative inline-block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 mr-2 py-2 text-sm font-medium text-white bg-blue rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
          />
        </svg>
        <span>My Score</span>
        <ChevronDownIcon
          className="w-4 h-4 transition-transform duration-200"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-auto">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-900">Saved Score Configurations</h3>
          </div>

          {/* Active Configuration Info */}
          {hasActiveWeights && (
            <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-900">Score Configuration Active</p>
                  <p className="text-xs text-blue-700 mt-1">
                    You have a custom score configuration applied. Use "Clear Active Weights" below to remove it.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Configure New Button */}
          <div className="px-4 py-3 border-b border-gray-200">
            <button
              onClick={() => {
                setIsOpen(false);
                onConfigureNew();
              }}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              + Configure New Score
            </button>
            {hasActiveWeights && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  onClearWeights();
                }}
                className="w-full mt-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                Clear Active Weights
              </button>
            )}
          </div>

          {/* Saved Scores List */}
          <div className="py-2">
            {savedScores.length === 0 && !isTrial ? (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-gray-500">No saved scores yet</p>
                <p className="text-xs text-gray-400 mt-1">Configure and save a score to see it here</p>
              </div>
            ) : savedScores.length > 0 ? (
              savedScores.map((score, index) => (
                <div
                  key={score.id || score.key || index}
                  className="px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate">{score.name}</h4>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(score.savedAt).toLocaleDateString()} at{' '}
                        {new Date(score.savedAt).toLocaleTimeString()}
                      </p>
                      {score.sourceWeights && score.sourceWeights.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {score.sourceWeights.slice(0, 3).map((sw, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600">{sw.name.replace('_', ' ')}</span>
                              <span className="font-medium text-gray-900">{sw.score.toFixed(2)}</span>
                            </div>
                          ))}
                          {score.sourceWeights.length > 3 && (
                            <p className="text-xs text-gray-400">
                              +{score.sourceWeights.length - 3} more sources
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setIsOpen(false);
                          onApplyScore(score);
                        }}
                        className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
